import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, ChevronRight } from 'lucide-react';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import ProductCard from '@/components/ProductCard';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Definizione struttura categorie e sottocategorie
const CATEGORIES = {
  petshop: {
    label: 'Pet Shop',
    subcategories: {
      cane: {
        label: 'Cane',
        subcategories: ['Cibo Umido', 'Cibo Secco']
      },
      gatto: {
        label: 'Gatto',
        subcategories: ['Cibo Secco', 'Cibo Umido']
      }
    }
  },
  giardinaggio: {
    label: 'Giardinaggio',
    subcategories: {}
  },
  varie: {
    label: 'Varie',
    subcategories: {}
  }
};

export default function ProductsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [category, setCategory] = useState(searchParams.get('category') || 'all');
  const [subcategory, setSubcategory] = useState(searchParams.get('subcategory') || 'all');

  useEffect(() => {
    fetchProducts();
  }, [category, subcategory, searchParams]);

  // Reset subcategory quando cambia categoria
  useEffect(() => {
    if (category !== 'petshop') {
      setSubcategory('all');
    }
  }, [category]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (category !== 'all') params.append('category', category);
      if (subcategory !== 'all') params.append('subcategory', subcategory);
      const searchQuery = searchParams.get('search');
      if (searchQuery) params.append('search', searchQuery);

      const response = await axios.get(`${API}/products?${params}`);
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const newParams = { category };
    if (search.trim()) newParams.search = search;
    if (subcategory !== 'all') newParams.subcategory = subcategory;
    setSearchParams(newParams);
    fetchProducts();
  };

  const handleCategoryChange = (value) => {
    setCategory(value);
    setSubcategory('all');
    const newParams = { category: value };
    if (search) newParams.search = search;
    setSearchParams(newParams);
  };

  const handleSubcategoryChange = (value) => {
    setSubcategory(value);
    const newParams = { category };
    if (value !== 'all') newParams.subcategory = value;
    if (search) newParams.search = search;
    setSearchParams(newParams);
  };

  // Ottieni le sottocategorie disponibili per la categoria selezionata
  const getAvailableSubcategories = () => {
    if (category === 'petshop' && CATEGORIES.petshop.subcategories) {
      const subs = [];
      Object.entries(CATEGORIES.petshop.subcategories).forEach(([key, value]) => {
        // Aggiungi la sottocategoria principale (es. Cane, Gatto)
        subs.push({ value: key, label: value.label, isParent: true });
        // Aggiungi le sotto-sottocategorie (es. Cibo Umido, Cibo Secco)
        value.subcategories.forEach(sub => {
          subs.push({ value: `${key}_${sub.toLowerCase().replace(' ', '_')}`, label: `${value.label} - ${sub}`, isParent: false });
        });
      });
      return subs;
    }
    return [];
  };

  const availableSubcategories = getAvailableSubcategories();

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <div className="container mx-auto px-4 md:px-6 max-w-7xl py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-extrabold mb-6" data-testid="products-page-title">I Nostri Prodotti</h1>

          <div className="flex flex-col md:flex-row gap-4">
            <form onSubmit={handleSearch} className="flex-1 flex gap-2">
              <Input
                placeholder="Cerca prodotti..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="flex-1"
                data-testid="search-input"
              />
              <Button type="submit" className="bg-rose-600 hover:bg-rose-700" data-testid="search-button">
                <Search className="h-4 w-4" />
              </Button>
            </form>

            <Select value={category} onValueChange={handleCategoryChange}>
              <SelectTrigger className="w-full md:w-48" data-testid="category-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le categorie</SelectItem>
                <SelectItem value="petshop">Pet Shop</SelectItem>
                <SelectItem value="giardinaggio">Giardinaggio</SelectItem>
                <SelectItem value="varie">Varie</SelectItem>
              </SelectContent>
            </Select>

            {/* Filtro Sottocategorie - Solo per Pet Shop */}
            {category === 'petshop' && availableSubcategories.length > 0 && (
              <Select value={subcategory} onValueChange={handleSubcategoryChange}>
                <SelectTrigger className="w-full md:w-56" data-testid="subcategory-select">
                  <SelectValue placeholder="Sottocategoria" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte le sottocategorie</SelectItem>
                  {availableSubcategories.map((sub) => (
                    <SelectItem 
                      key={sub.value} 
                      value={sub.value}
                      className={sub.isParent ? 'font-semibold' : 'pl-6'}
                    >
                      {sub.isParent ? sub.label : `└ ${sub.label.split(' - ')[1]}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Breadcrumb filtri attivi */}
          {(category !== 'all' || subcategory !== 'all') && (
            <div className="flex items-center gap-2 mt-4 text-sm text-gray-600">
              <span>Filtri:</span>
              {category !== 'all' && (
                <span className="bg-rose-100 text-rose-700 px-2 py-1 rounded-full">
                  {CATEGORIES[category]?.label || category}
                </span>
              )}
              {subcategory !== 'all' && (
                <>
                  <ChevronRight className="h-4 w-4" />
                  <span className="bg-rose-100 text-rose-700 px-2 py-1 rounded-full">
                    {availableSubcategories.find(s => s.value === subcategory)?.label || subcategory}
                  </span>
                </>
              )}
              <button 
                onClick={() => {
                  setCategory('all');
                  setSubcategory('all');
                  setSearchParams({});
                }}
                className="text-rose-600 hover:underline ml-2"
              >
                Rimuovi filtri
              </button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Caricamento...</p>
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-12" data-testid="no-products">
            <p className="text-gray-500">Nessun prodotto trovato</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="products-grid">
            {products.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}